import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Panel } from "../admin/fields";

const TONE = { ERROR: "bg-red-100 text-red-700", CRITICAL: "bg-red-600 text-white", WARNING: "bg-gold/20 text-ink" };

const Row = ({ l }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-b border-ink/5 py-3 last:border-0" data-testid={`log-row-${l.id}`}>
      <button type="button" className="flex w-full items-start gap-3 text-left" onClick={() => setOpen(!open)} data-testid={`log-toggle-${l.id}`}>
        <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold ${TONE[l.level] || "bg-ink/10"}`}>{l.level}</span>
        <span className="min-w-0 flex-1">
          <span className="block break-words text-sm text-ink">{l.message}</span>
          <span className="mt-0.5 block font-mono text-[11px] text-ink/50">
            {new Date(l.at).toLocaleString()} · {l.method} {l.path || "background"}{l.site ? ` · site ${l.site}` : ""}{l.ref ? ` · ref ${l.ref}` : ""}
          </span>
        </span>
        {l.traceback && <ChevronDown className={`h-4 w-4 shrink-0 text-ink/40 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && l.traceback && <pre className="mt-2 max-h-80 overflow-auto rounded-lg bg-ink p-3 font-mono text-[11px] leading-relaxed text-white/85" data-testid={`log-trace-${l.id}`}>{l.traceback}</pre>}
    </li>
  );
};

export const LogsPanel = () => {
  const [f, setF] = useState({ level: "", q: "" });
  const qc = useQueryClient();
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["system-logs", f],
    queryFn: () => api.get("/platform/logs", { params: { ...f, limit: 200 } }).then((r) => r.data),
  });
  const clear = async () => {
    if (!window.confirm("Delete all system logs?")) return;
    try {
      await api.delete("/platform/logs");
      qc.invalidateQueries({ queryKey: ["system-logs"] });
      toast.success("Logs cleared");
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <Panel title="System logs" hint="Errors and warnings from every site, newest first, kept for 30 days. Open an entry to see exactly where in the code it came from. Guests' error messages show a ref you can search for here.">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {["", "ERROR", "WARNING"].map((lv) => (
          <button key={lv} type="button" onClick={() => setF({ ...f, level: lv })} data-testid={`logs-level-${lv || "all"}`}
            className={`rounded-full px-3 py-1 text-xs ${f.level === lv ? "bg-brand text-white" : "bg-ink/5 text-ink/70"}`}>{lv || "All"}</button>
        ))}
        <input className="field-input !h-9 min-w-[180px] flex-1" placeholder="Search message, path, ref" value={f.q}
          onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="logs-search" />
        <button type="button" className="btn-ghost" onClick={() => refetch()} data-testid="logs-refresh">
          {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}Refresh
        </button>
        <button type="button" className="btn-ghost !text-red-600" onClick={clear} data-testid="logs-clear"><Trash2 className="h-4 w-4" />Clear</button>
      </div>
      {data?.length === 0 && <p className="text-sm text-ink/55" data-testid="logs-empty">No errors logged. All quiet.</p>}
      <ul className="max-h-[560px] overflow-y-auto" data-testid="logs-list">{data?.map((l) => <Row key={l.id} l={l} />)}</ul>
    </Panel>
  );
};
